import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from '../../../../services/analytics.service';

@Component({
  selector: 'app-date-range-control',
  templateUrl: './date-range-control.component.html',
  styleUrl: './date-range-control.component.scss',
})
export class DateRangeControlComponent {
  @Input() analyticsPage:
    | 'sessions'
    | 'events'
    | 'semantics'
    | 'live' = 'sessions';

  dateRange: number = 7;
  interval = 'day';

  readonly presetRanges = [7, 30, 90, 365];

  constructor(
    public analyticsService: AnalyticsService,
    protected t: TranslateService
  ) {
    this.dateRange = this.analyticsService.dateRange || 7;
    this.interval = this.analyticsService.interval || this.getInterval();

    this.analyticsService.fetchRetention();
    this.analyticsService.retentionDays$.subscribe((days) => {
      // Clamp a stored selection that exceeds the plan's retention window
      if (days !== null && this.dateRange > days) {
        this.dateRange = this.largestAllowedRange(days);
        this.interval = this.getInterval();
        localStorage.setItem(
          'al-analytics-date-range',
          this.dateRange.toString()
        );
        localStorage.setItem('al-analytics-interval', this.interval);
        this.applyDateRange();
      }
    });
  }

  isRangeDisabled(days: number): boolean {
    const retention = this.analyticsService.retentionDays$.value;
    return retention !== null && days > retention;
  }

  get anyRangeDisabled(): boolean {
    return this.presetRanges.some((d) => this.isRangeDisabled(d));
  }

  get retentionTooltip(): string {
    return this.t.instant('pages.analytics.retention.limitTooltip', {
      days: this.analyticsService.retentionDays$.value,
    });
  }

  private largestAllowedRange(retentionDays: number): number {
    const allowed = this.presetRanges.filter((d) => d <= retentionDays);
    return allowed.length ? allowed[allowed.length - 1] : 7;
  }

  onDateRangeSelect(event: any[]) {
    console.log('Selected date range:', event);
    const range = event[0];

    if (this.dateRange == range) {
      return;
    }

    this.dateRange = parseInt(range as unknown as string, 10);
    this.interval = this.getInterval();

    localStorage.setItem('al-analytics-date-range', this.dateRange.toString());
    localStorage.setItem('al-analytics-interval', this.interval);

    this.applyDateRange();
  }

  getInterval(): 'day' | 'week' | 'month' {
    if (this.dateRange <= 7) {
      return 'day';
    } else if (this.dateRange <= 30) {
      return 'day';
    } else if (this.dateRange <= 90) {
      return 'week';
    } else {
      return 'month';
    }
  }

  applyDateRange() {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - this.dateRange + 1); // +1 to include today

    const formatDate = (date: Date) => {
      return date.toISOString().split('T')[0];
    };

    this.analyticsService.dateFrom = formatDate(from);
    // this.analyticsService.dateTo = formatDate(to);
    this.analyticsService.dateRange = this.dateRange;
    this.analyticsService.interval = this.getInterval();

    // Trigger data refresh in the respective component
    if (this.analyticsPage === 'sessions') {
      this.analyticsService.fetchSessionsData();
    } else if (this.analyticsPage === 'events') {
      this.analyticsService.fetchEventsData();
    } else if (this.analyticsPage === 'semantics') {
      this.analyticsService.fetchSemanticsData();
    } else if (this.analyticsPage === 'live') {
      this.analyticsService.fetchLiveData();
    }
  }
}
